import * as React from 'react';
import { useParams } from 'react-router';

import { DateTime } from 'luxon';

import { Map } from '@admin/components/helpers/map';
import { Table as HistoriesTable } from '@admin/components/histories/table';
import { Spinner } from '@admin/components/spinner';
import { jobAvailabilityListURL, regionEditURL, regionURL, regionsURL } from '@admin/config/routes';
import { usePermissionKey } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useModifyRegionMutation, useRegionQuery, History__Subject } from '@admin/schema';
import { BreadcrumbCurrent } from '@admin/styled/breadcrumb_current';
import { BreadcrumbNav } from '@admin/styled/breadcrumb_nav';
import { BreadcrumbSep } from '@admin/styled/breadcrumb_sep';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { DetailGrid } from '@admin/styled/detail_grid';
import { DetailItem } from '@admin/styled/detail_item';
import { DetailLabel } from '@admin/styled/detail_label';
import { DetailValue } from '@admin/styled/detail_value';
import { MutedPanel } from '@admin/styled/muted_panel';
import { MutedText } from '@admin/styled/muted_text';
import { Page } from '@admin/styled/page';
import { PageHeader } from '@admin/styled/page_header';
import { PageSections } from '@admin/styled/page_sections';
import { PageTitle } from '@admin/styled/page_title';
import { Pill } from '@admin/styled/pill';
import { Section } from '@admin/styled/section';
import { SectionActions } from '@admin/styled/section_actions';
import { SectionTitle } from '@admin/styled/section_title';

import { FeatureFlagsList } from '../feature_flags/list';
import { OperatingSchedule } from '../operating_schedule';
import { RegionFacilities } from './region_facilities';
import { UpsertModal } from './upsert_modal';
import { displayableText } from './utils';

function formatDate(iso: string) {
  return iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED) : 'N/A';
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <DetailItem>
      <DetailLabel>{label}</DetailLabel>
      <DetailValue>{value ?? <MutedText>Not set</MutedText>}</DetailValue>
    </DetailItem>
  );
}

export const Details: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showUpsertModal, setShowUpsertModal] = React.useState<boolean>(false);

  const { data, refetch, loading } = useRegionQuery({ client, variables: { regionID: String(id) } });
  const region = data?.region;

  useTitle(region?.name ?? 'Region');

  const [modifyRegion, { loading: modifyingRegion }] = useModifyRegionMutation({
    client,
    onCompleted: (result) => {
      if (!result.modifyRegion?.error) {
        void refetch();
      }
    },
  });
  const canUpdate = usePermissionKey('facility:update');

  if (loading && !data) {
    return <Spinner />;
  }

  if (!region || !id) {
    return (
      <Page>
        <MutedPanel>Region not found.</MutedPanel>
      </Page>
    );
  }

  return (
    <Page>
      <BreadcrumbNav aria-label="Breadcrumb">
        <ButtonLink to={regionsURL()}>Regions</ButtonLink>
        <BreadcrumbSep>/</BreadcrumbSep>
        <BreadcrumbCurrent>{region.name}</BreadcrumbCurrent>
      </BreadcrumbNav>

      <PageHeader>
        <PageTitle>{region.name}</PageTitle>
      </PageHeader>

      <PageSections>
        <Section>
          <SectionTitle>Operating schedule</SectionTitle>
          <OperatingSchedule
            operatingScheduleTemplates={region.operatingScheduleTemplates}
            manageHref={jobAvailabilityListURL()}
          />
        </Section>

        <Section>
          <SectionTitle>Feature flags</SectionTitle>
          <FeatureFlagsList
            resourceName="Region"
            resourceID={region.id}
            editAccess={canUpdate}
            emptyStateMessage="No feature flags are configured for this region yet."
          />
        </Section>

        <Section>
          <RegionFacilities
            regionID={region.id}
            defaultWarehouse={region.defaultWarehouse}
            defaultDepot={region.defaultDepot}
            facilities={region.facilities}
            modifyingRegion={modifyingRegion}
            onUpdated={() => {
              void refetch();
            }}
          />
        </Section>

        <Section>
          <Card>
            <CardToolbar>
              <SectionTitle>Other details</SectionTitle>
              {canUpdate && (
                <SectionActions>
                  <Button
                    variant="danger"
                    size="sm"
                    type="button"
                    disabled={modifyingRegion}
                    onClick={() => {
                      modifyRegion({
                        variables: {
                          id,
                          input: {
                            active: !region.active,
                          },
                        },
                      });
                    }}
                  >
                    {region.active ? 'Deactivate region' : 'Activate region'}
                  </Button>
                  <Button variant="secondary" size="sm" type="button" onClick={() => setShowUpsertModal(true)}>
                    Edit details
                  </Button>
                  <ButtonLink variant="secondary" size="sm" to={regionEditURL({ id })}>
                    Edit zip codes
                  </ButtonLink>
                </SectionActions>
              )}
            </CardToolbar>
            <DetailGrid>
              {region.parent && (
                <DetailField
                  label="Parent"
                  value={<ButtonLink to={regionURL({ id: region.parent.id })}>{region.parent.name}</ButtonLink>}
                />
              )}
              <DetailField
                label="Active"
                value={<Pill tone={region.active ? 'success' : 'muted'}>{region.active ? 'Active' : 'Inactive'}</Pill>}
              />
              <DetailField label="IRM cluster" value={displayableText(region.irmCluster)} />
              <DetailField label="IRM territory" value={displayableText(region.irmTerritory)} />
              <DetailField label="P&L region" value={displayableText(region.plRegion)} />
              <DetailField label="Clutter geo" value={displayableText(region.geo)} />
              <DetailField label="Fulfiller" value={displayableText(region.fulfiller)} />
              <DetailField label="Timezone" value={region.tz} />
              <DetailField label="Default warehouse" value={region.defaultWarehouse?.name} />
              <DetailField label="Netsuite region ID" value={`${region.netsuiteRegionID}`} />
              <DetailField
                label="Shift offer booking threshold"
                value={
                  region.shiftOfferBookingThreshold != null ? `${region.shiftOfferBookingThreshold}%` : 'Not configured'
                }
              />
              <DetailField label="Hex color" value={region.hexColor} />
              <DetailField label="Created" value={formatDate(region.createdAt)} />
              <DetailField label="Last updated" value={formatDate(region.updatedAt)} />
              <DetailItem style={{ gridColumn: '1 / -1' }}>
                <DetailLabel>In service area zip codes</DetailLabel>
                <DetailValue>{region.zipCodes.join(', ') || <MutedText>None</MutedText>}</DetailValue>
              </DetailItem>
              <DetailItem style={{ gridColumn: '1 / -1' }}>
                <DetailLabel>Storage extended service area zip codes</DetailLabel>
                <DetailValue>
                  {region.storageExtendedServiceAreaPostalCodes.join(', ') || <MutedText>None</MutedText>}
                </DetailValue>
              </DetailItem>
              <DetailItem style={{ gridColumn: '1 / -1' }}>
                <DetailLabel>Moving extended service area zip codes</DetailLabel>
                <DetailValue>
                  {region.movingExtendedServiceAreaPostalCodes.join(', ') || <MutedText>None</MutedText>}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
        </Section>

        <Section>
          <SectionTitle>Map</SectionTitle>
          <Card>
            <Map center={region.center} marker={region.center} />
          </Card>
        </Section>

        <Section>
          <SectionTitle>History</SectionTitle>
          <Card>
            <div style={{ padding: '12px 16px 16px' }}>
              <HistoriesTable id={id} type={History__Subject.Region} />
            </div>
          </Card>
        </Section>
      </PageSections>

      {showUpsertModal && data?.region && (
        <UpsertModal
          region={data.region}
          warehouseOptions={data.warehouses}
          onClose={() => {
            setShowUpsertModal(false);
          }}
        />
      )}
    </Page>
  );
};
